
import React, { useState } from "react";
import {
    Button,
    Checkbox,
    Form,
    Input,
    Typography,
    message,
} from "antd";
import {
    PhoneOutlined,
    LockOutlined,
    GlobalOutlined,
    ArrowLeftOutlined,
    EyeInvisibleOutlined,
    EyeTwoTone,
} from "@ant-design/icons";
import "./QuranRazaviLogin.css";

const { Text, Link } = Typography;

const QuranRazaviLogin = ({
                              onLogin,
                              onRegister,
                              onForgotPassword,
                              websiteUrl = "https://quranlove.ir",
                              phoneNumber = "05132290876",
                              logo = "/images/quran-razavi-logo.png",
                          }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            if (onLogin) {
                await onLogin({
                    phone: values.phone,
                    password: values.password,
                    remember: values.remember ?? false,
                });
            } else {
                message.info("تابع ورود را به API خود متصل کنید.");
            }
        } catch (error) {
            message.error(
                error?.message || "ورود انجام نشد. لطفاً دوباره تلاش کنید."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <main
            dir="rtl"
            className="qr-login-page min-h-screen w-full flex items-center justify-center p-3 sm:p-6"
        >

            <section className="qr-login-card relative w-full max-w-3xl overflow-hidden rounded-[32px]">
                {/* Decorative ornaments */}
                <div
                    className="qr-ornament qr-ornament-top"
                    aria-hidden="true"
                />

                <div
                    className="qr-ornament qr-ornament-bottom"
                    aria-hidden="true"
                />

                <div className="relative z-10 px-5 py-8 sm:px-12 sm:py-12 md:px-16">
                    {/* Logo */}
                    <div className="mb-6 flex justify-center">
                        <img
                            src={logo}
                            alt="لوگوی مرکز قرآن حرم مطهر رضوی"
                            className="qr-login-logo"
                        />
                    </div>

                    {/* Heading */}
                    <header className="mb-7 text-center">
                        <h1 className="qr-login-title">
                            مرکز قرآن حرم مطهر رضوی
                        </h1>

                        <div className="qr-divider my-6">
                            <span/>
                            <i/>
                            <span/>
                        </div>

                        <p className="qr-login-description">
                            برای ورود به سامانه قرآن رضوی، لطفاً اطلاعات
                            حساب کاربری خود را وارد کنید.
                        </p>
                    </header>

                    {/* Login form */}
                    <Form
                        form={form}
                        layout="vertical"
                        requiredMark={false}
                        onFinish={handleSubmit}
                        autoComplete="on"
                        className="qr-login-form"
                    >
                        <Form.Item
                            name="phone"
                            rules={[
                                {
                                    required: true,
                                    message: "شماره تلفن همراه را وارد کنید.",
                                },
                                {
                                    pattern: /^09\d{9}$/,
                                    message:
                                        "شماره موبایل باید با 09 شروع شده و 11 رقم باشد.",
                                },
                            ]}
                        >
                            <Input
                                size="large"
                                inputMode="numeric"
                                autoComplete="tel"
                                maxLength={11}
                                placeholder="شماره تلفن همراه (مثال: 09123456789)"
                                prefix={
                                    <span className="qr-input-icon">
                                        <PhoneOutlined/>
                                    </span>
                                }
                                className="qr-login-input"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: "رمز عبور را وارد کنید.",
                                },
                            ]}
                        >
                            <Input.Password
                                size="large"
                                autoComplete="current-password"
                                placeholder="رمز عبور"
                                iconRender={(visible) =>
                                    visible ? (
                                        <EyeTwoTone/>
                                    ) : (
                                        <EyeInvisibleOutlined/>
                                    )
                                }
                                prefix={
                                    <span className="qr-input-icon">
                                        <LockOutlined/>
                                    </span>
                                }
                                className="qr-login-input"
                            />
                        </Form.Item>

                        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                            <Form.Item
                                name="remember"
                                valuePropName="checked"
                                initialValue={true}
                                noStyle
                            >
                                <Checkbox className="qr-remember">
                                    مرا به خاطر بسپار
                                </Checkbox>
                            </Form.Item>

                            <button
                                type="button"
                                className="qr-text-link"
                                onClick={onForgotPassword}
                            >
                                فراموشی رمز عبور؟
                            </button>
                        </div>

                        <Button
                            htmlType="submit"
                            type="primary"
                            size="large"
                            block
                            loading={loading}
                            className="qr-login-button"
                        >
                            <span>ورود به سامانه</span>
                            <ArrowLeftOutlined/>
                        </Button>

                        <div className="mt-6 text-center">
                            <Text className="qr-register-text">
                                هنوز ثبت‌نام نکرده‌اید؟
                            </Text>{" "}
                            <button
                                type="button"
                                onClick={onRegister}
                                className="qr-text-link font-bold"
                            >
                                ثبت‌نام در مرکز قرآن
                            </button>
                        </div>
                    </Form>

                    {/* Decorative divider */}
                    <div className="qr-divider my-9">
                        <span/>
                        <i/>
                        <span/>
                    </div>

                    {/* Contact information */}
                    <footer className="qr-contact">
                        <h2 className="qr-contact-title">
                            اطلاعات تماس
                        </h2>

                        <a
                            href={websiteUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="qr-contact-item"
                        >
                            <span className="qr-contact-icon">
                                <GlobalOutlined/>
                            </span>

                            <span className="flex min-w-0 flex-col gap-1">
                                <span className="qr-contact-label">
                                    وب‌سایت
                                </span>
                                <span className="qr-contact-value break-all">
                                    {websiteUrl.replace(/^https?:\/\//, "")}
                                </span>
                            </span>
                        </a>

                        <a
                            href={`tel:${phoneNumber}`}
                            className="qr-contact-item"
                        >
                            <span className="qr-contact-icon">
                                <PhoneOutlined/>
                            </span>

                            <span className="flex flex-col gap-1">
                                <span className="qr-contact-label">
                                    تلفن تماس
                                </span>
                                <span
                                    className="qr-contact-value"
                                    dir="ltr"
                                >
                                    {phoneNumber}
                                </span>
                            </span>
                        </a>
                    </footer>
                </div>
            </section>
        </main>
    )
        ;
};

export default QuranRazaviLogin;
